import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CATEGORY_OPTIONS,
  CITY_OPTIONS,
  DATE_OPTIONS,
  SEARCH_PLACEHOLDER,
  emptySearch,
  searchTarget,
  type FilterOption,
  type SearchField,
  type SearchFilters,
} from '@/lib/searchBar';

/**
 * SearchBar — buscador principal de la tienda (Home).
 *
 * La **lógica** (qué filtros hay, cuándo hay algo que buscar, a qué URL se va)
 * vive en `@/lib/searchBar` y se verifica sin React. Acá queda sólo la
 * presentación + el estado local, como pide el diseño: los valores quedan
 * capturados y listos para conectarse al backend después.
 *
 * **Diseño premium (RESTYLE 2026-09-05):** integrado con el lenguaje del Home —
 * superficie blanca, radio suave, sombra discreta y `ring` tenue; tres filtros
 * (Ciudad → Fecha → Categoría) con el icono en un chip azul Mi Boleta
 * (`bg-primary/10`), separadores muy tenues, input con lupa, y el botón al
 * extremo derecho con gradiente **azul Mi Boleta → cian** (mismo lenguaje del
 * Hero). Sin colores fuera de la paleta; hover/focus sutiles.
 *
 * **Responsive:** barra horizontal en desktop (lg+); en tablet y móvil
 * **colapsa a columna** (los separadores se ocultan, cada control ocupa el
 * ancho, el botón muestra su etiqueta) — una barra horizontal apretada en un
 * teléfono o tablet es intocable.
 */

// Iconos SVG inline — el proyecto no usa librería de iconos (mismo patrón que
// HomeHero / Sidebar).
function PinIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function GridIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

function CalendarIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

function ChevronDown(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-neutral-400"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function SearchIcon({ className = '' }: { className?: string }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

interface FilterSelectProps {
  icon: JSX.Element;
  label: string;
  value: string;
  options: readonly FilterOption[];
  onChange: (value: string) => void;
}

/**
 * Un filtro desplegable. Es un `<select>` nativo estilizado, no un menú a mano:
 * el nativo trae accesibilidad, teclado y el picker del sistema en móvil gratis
 * — reimplementarlo sería peor en todos los dispositivos. El icono y el chevron
 * se superponen; el `<select>` transparente va encima y captura el clic.
 *
 * Premium (RESTYLE, sólo estético): el icono va en un chip suave azul Mi Boleta
 * (`bg-primary/10 text-primary`), coherente con los chips del Hero; hover y
 * focus tiñen el bloque muy sutilmente (`bg-neutral-50`), sin bordes fuertes.
 */
function FilterSelect({ icon, label, value, options, onChange }: FilterSelectProps): JSX.Element {
  return (
    <label className="group relative flex min-w-0 flex-1 items-center gap-2.5 px-4 py-3.5 transition-colors duration-150 focus-within:bg-neutral-50 hover:bg-neutral-50">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </span>
      <span className="sr-only">{label}</span>
      <span
        className={`min-w-0 flex-1 truncate text-sm font-medium ${value ? 'text-neutral-900' : 'text-neutral-500'}`}
      >
        {options.find((o) => o.value === value)?.label ?? label}
      </span>
      <ChevronDown />
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {options.map((o) => (
          <option key={o.value || 'any'} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Separador vertical muy sutil — sólo en la barra horizontal (desktop, lg+). */
function Divider(): JSX.Element {
  return <span aria-hidden className="hidden w-px self-stretch bg-neutral-200/80 lg:block" />;
}

export function SearchBar(): JSX.Element {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<SearchFilters>(emptySearch);

  function setField(field: SearchField, value: string): void {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    // Listo para el backend: hoy navega al catálogo con la query armada. El
    // destino sale de la lógica pura (`searchTarget`), no se arma acá.
    navigate(searchTarget(filters));
  }

  return (
    <form
      onSubmit={handleSubmit}
      role="search"
      aria-label="Buscar eventos"
      className="flex w-full flex-col overflow-hidden rounded-2xl bg-white shadow-lg shadow-primary-900/5 ring-1 ring-neutral-200/70 lg:flex-row lg:items-stretch"
    >
      {/* Orden: Ciudad → Fecha → Categoría → buscador (RESTYLE 2026-09-05). */}
      <FilterSelect
        icon={<PinIcon />}
        label="Ciudad"
        value={filters.city}
        options={CITY_OPTIONS}
        onChange={(v) => setField('city', v)}
      />
      <Divider />
      <FilterSelect
        icon={<CalendarIcon />}
        label="Fecha"
        value={filters.date}
        options={DATE_OPTIONS}
        onChange={(v) => setField('date', v)}
      />
      <Divider />
      <FilterSelect
        icon={<GridIcon />}
        label="Categoría"
        value={filters.category}
        options={CATEGORY_OPTIONS}
        onChange={(v) => setField('category', v)}
      />
      <Divider />

      {/* Input principal — ocupa el espacio restante. */}
      <div className="flex flex-[1.4] items-center gap-2.5 px-4 py-3.5 transition-colors duration-150 focus-within:bg-neutral-50">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-primary">
          <SearchIcon />
        </span>
        <input
          type="search"
          value={filters.query}
          onChange={(e) => setField('query', e.target.value)}
          placeholder={SEARCH_PLACEHOLDER}
          aria-label={SEARCH_PLACEHOLDER}
          className="min-w-0 flex-1 border-0 bg-transparent text-sm font-medium text-neutral-900 placeholder:font-normal placeholder:text-neutral-400 focus:outline-none"
        />
      </div>

      {/* Botón acoplado al extremo derecho: sin separación, gradiente de marca
          (azul Mi Boleta → cian, mismo lenguaje del Hero). Presencia clara,
          contraste alto, hover y focus visibles, tamaño táctil. */}
      <button
        type="submit"
        aria-label="Buscar"
        className="flex shrink-0 items-center justify-center gap-2 bg-gradient-to-br from-primary to-accent px-6 py-4 text-white transition-[filter] duration-150 hover:brightness-[1.06] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary lg:px-8 lg:py-3"
      >
        <SearchIcon className="text-white" />
        <span className="text-sm font-semibold lg:hidden">Buscar</span>
      </button>
    </form>
  );
}
